import React from 'react';
import { Card } from 'react-bootstrap';
import Chart from 'react-apexcharts';
import type { FeedbackAnalyticsDto } from '../../../../services/feedback-analytics';
import InfoHint from './InfoHint';

interface TrendChartProps {
  data: FeedbackAnalyticsDto | null;
  loading?: boolean;
}

interface TrendData {
  date: string;
  averageRating: number;
  npsScore: number;
  totalReviews: number;
}

export const TrendChart: React.FC<TrendChartProps> = ({
  data,
  loading = false,
}) => {
  if (!data || data.trendData.length === 0) {
    return (
      <div className="alert alert-info text-center my-4">
        Nenhum dado de tendência disponível
      </div>
    );
  }

  const chartOptions: ApexCharts.ApexOptions = {
    chart: {
      type: 'area',
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
      },
      sparkline: {
        enabled: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: 'smooth',
      width: 2,
    },
    xaxis: {
      categories: data.trendData.map((t: TrendData) => t.date),
      title: {
        text: 'Data',
      },
      type: 'datetime',
    },
    yaxis: [
      {
        title: {
          text: 'Avaliação Média',
        },
        min: 0,
        max: 10,
      },
      {
        opposite: true,
        title: {
          text: 'Total de Avaliações',
        },
      },
      {
        title: {
          text: 'NPS',
        },
        min: -100,
        max: 100,
      },
    ],
    colors: ['#ffc107', '#0d6efd', '#198754'],
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.45,
        opacityTo: 0.05,
        stops: [20, 100, 100, 100],
      },
    },
    tooltip: {
      theme: 'light',
      shared: true,
      intersect: false,
      x: {
        format: 'dd/MM/yyyy',
      },
    },
  };

  const chartSeries = [
    {
      name: 'Avaliação Média',
      data: data.trendData.map((t: TrendData) => t.averageRating),
    },
    {
      name: 'Total de Avaliações',
      data: data.trendData.map((t: TrendData) => t.totalReviews),
    },
    {
      name: 'NPS',
      data: data.trendData.map((t: TrendData) => t.npsScore),
    },
  ];

  return (
    <Card className="shadow-sm border-0 mb-5">
      <Card.Header className="bg-white border-bottom">
        <Card.Title className="mb-0 d-flex align-items-center">
          Tendência de Avaliações e NPS
          <InfoHint
            id="trend-rating-nps"
            text="Exibe a evolução diária da avaliação média, do volume de reviews e do NPS ao longo do tempo."
          />
        </Card.Title>
      </Card.Header>
      <Card.Body>
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
          </div>
        ) : (
          <Chart
            options={chartOptions}
            series={chartSeries}
            type="area"
            height={400}
          />
        )}
      </Card.Body>
    </Card>
  );
};

export default TrendChart;
